import type { Editor } from '@tiptap/core';
import { icon, type IconName } from '@/icons/icons';
import { store } from '@/state/store';
import { MARGIN_PRESETS, DEFAULT_PAGE_NUMBER, type PageSetup, type PageNumberSetup } from '@/state/types';
import { FormatPainter } from '@/editor/formatPainter';
import { pickImage, initialSize } from '@/files/imagePicker';
import { SYMBOL_CATEGORIES } from '@/editor/symbols';
import {
  getMarkupView, setMarkupView, MARKUP_VIEW_LABELS,
  acceptAtSelection, rejectAtSelection, goToNextChange, goToPrevChange,
} from '@/editor/trackChangesService';
import type { MarkupView } from '@/state/types';

// ═══════════════════════════════════════════════════════════════
// Ribbon (sekce 2.5) — karty Domů a Rozložení pro MVP.
// Ikony Fluent System Icons (viz src/icons). Vzhled a členění dle Wordu.
// ═══════════════════════════════════════════════════════════════

const FONTS = ['Calibri', 'Arial', 'Times New Roman', 'Georgia', 'Verdana', 'Courier New', 'Cambria'];
const SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 72];

type OnFindReplace = () => void;
type OnComment = () => void;
type OnToggleComments = () => void;
type OnToggleTracking = () => void;
type OnToggleReview = () => void;

export function buildRibbon(
  root: HTMLElement,
  editor: Editor,
  painter: FormatPainter,
  onFindReplace: OnFindReplace,
  onComment: OnComment,
  onToggleComments: OnToggleComments,
  onToggleTracking: OnToggleTracking,
  onToggleReview: OnToggleReview,
): void {
  root.innerHTML = '';
  root.className = 'ribbon';

  // ── Záložky karet ──────────────────────────────────────────
  const tabs = el('div', 'ribbon-tabs');
  const panes = el('div', 'ribbon-panes');
  const tabDefs = [
    { id: 'home', label: 'Domů' },
    { id: 'insert', label: 'Vložení' },
    { id: 'layout', label: 'Rozložení' },
    { id: 'review', label: 'Revize' },
  ];
  const paneEls: Record<string, HTMLElement> = {};

  tabDefs.forEach((t, i) => {
    const btn = el('button', 'ribbon-tab');
    btn.textContent = t.label;
    btn.onclick = () => selectTab(t.id);
    tabs.appendChild(btn);
    const pane = el('div', 'ribbon-pane');
    pane.dataset.tab = t.id;
    paneEls[t.id] = pane;
    panes.appendChild(pane);
    (btn as any)._tab = t.id;
    if (i === 0) btn.classList.add('active');
  });

  function selectTab(id: string) {
    tabs.querySelectorAll('.ribbon-tab').forEach((b) => b.classList.toggle('active', (b as any)._tab === id));
    Object.entries(paneEls).forEach(([k, p]) => (p.style.display = k === id ? 'flex' : 'none'));
  }

  buildHomePane(paneEls.home, editor, painter, onFindReplace);
  buildInsertPane(paneEls.insert, editor, onComment, onToggleComments);
  buildReviewPane(paneEls.review, editor, onComment, onToggleTracking, onToggleReview);
  buildLayoutPane(paneEls.layout);
  selectTab('home');

  root.appendChild(tabs);
  root.appendChild(panes);

  // Reaktivně aktualizuj stav tlačítek dle výběru.
  editor.on('selectionUpdate', () => refreshActiveStates(root, editor));
  editor.on('transaction', () => refreshActiveStates(root, editor));
}

// ── Karta Domů ───────────────────────────────────────────────
function buildHomePane(pane: HTMLElement, editor: Editor, painter: FormatPainter, onFindReplace: OnFindReplace) {
  pane.style.display = 'flex';

  // Skupina: Zpět/Znovu
  pane.appendChild(group('Historie', [
    iconBtn('undo', 'Zpět (Ctrl+Z)', () => editor.chain().focus().undo().run()),
    iconBtn('redo', 'Znovu (Ctrl+Y)', () => editor.chain().focus().redo().run()),
  ]));

  // Skupina: Schránka
  pane.appendChild(group('Schránka', [
    iconBtn('paste', 'Vložit', async () => {
      try {
        const text = await navigator.clipboard.readText();
        editor.chain().focus().insertContent(text).run();
      } catch { /* clipboard perm */ }
    }),
    iconBtn('cut', 'Vyjmout', async () => {
      const { from, to } = editor.state.selection;
      const text = editor.state.doc.textBetween(from, to, ' ');
      if (text) { try { await navigator.clipboard.writeText(text); } catch {} editor.chain().focus().deleteSelection().run(); }
    }),
    iconBtn('copy', 'Kopírovat', async () => {
      const { from, to } = editor.state.selection;
      const text = editor.state.doc.textBetween(from, to, ' ');
      if (text) { try { await navigator.clipboard.writeText(text); } catch {} }
    }),
    toggleBtn('formatPainter', 'Kopírovat formát', 'painter', {
      onClick: () => painter.activateOnce(),
      onDblClick: () => painter.activatePersistent(),
    }),
  ]));

  // Skupina: Písmo
  const fontSel = dropdown(FONTS, 'Times New Roman', (v) => editor.chain().focus().setFontFamily(v).run(), 150);
  fontSel.dataset.role = 'font';
  const sizeSel = dropdown(SIZES.map(String), '11', (v) => setFontSize(editor, v), 64);
  sizeSel.dataset.role = 'size';
  const fontRow1 = el('div', 'ribbon-row');
  fontRow1.append(fontSel, sizeSel);
  const fontRow2 = el('div', 'ribbon-row');
  fontRow2.append(
    markBtn('bold', 'Tučné (Ctrl+B)', 'bold', editor),
    markBtn('italic', 'Kurzíva (Ctrl+I)', 'italic', editor),
    markBtn('underline', 'Podtržení (Ctrl+U)', 'underline', editor),
    markBtn('strikethrough', 'Přeškrtnuté', 'strike', editor),
    markBtn('subscript', 'Dolní index', 'subscript', editor),
    markBtn('superscript', 'Horní index', 'superscript', editor),
    colorBtn('textColor', 'Barva textu', (c) => editor.chain().focus().setColor(c).run()),
    colorBtn('highlight', 'Zvýraznění', (c) => editor.chain().focus().setHighlight({ color: c }).run()),
  );
  const fontGroup = el('div', 'ribbon-group');
  const fontStack = el('div', 'ribbon-stack');
  fontStack.append(fontRow1, fontRow2);
  fontGroup.append(fontStack, groupLabel('Písmo'));
  pane.appendChild(fontGroup);

  // Skupina: Odstavec
  const paraRow1 = el('div', 'ribbon-row');
  paraRow1.append(
    cmdBtn('bulletList', 'Odrážky', () => editor.chain().focus().toggleBulletList().run()),
    cmdBtn('numberedList', 'Číslování', () => editor.chain().focus().toggleOrderedList().run()),
    cmdBtn('indentDecrease', 'Zmenšit odsazení', () => {
      if (editor.isActive('listItem')) editor.chain().focus().liftListItem('listItem').run();
      else editor.chain().focus().indentLess().run();
    }),
    cmdBtn('indentIncrease', 'Zvětšit odsazení', () => {
      if (editor.isActive('listItem')) editor.chain().focus().sinkListItem('listItem').run();
      else editor.chain().focus().indentMore().run();
    }),
  );
  const paraRow2 = el('div', 'ribbon-row');
  paraRow2.append(
    alignBtn('alignLeft', 'Vlevo', 'left', editor),
    alignBtn('alignCenter', 'Na střed', 'center', editor),
    alignBtn('alignRight', 'Vpravo', 'right', editor),
    alignBtn('alignJustify', 'Do bloku', 'justify', editor),
  );
  const paraGroup = el('div', 'ribbon-group');
  const paraStack = el('div', 'ribbon-stack');
  paraStack.append(paraRow1, paraRow2);
  paraGroup.append(paraStack, groupLabel('Odstavec'));
  pane.appendChild(paraGroup);

  // Skupina: Styly
  const styleSel = dropdown(
    ['Normální', 'Nadpis 1', 'Nadpis 2', 'Nadpis 3', 'Nadpis 4', 'Nadpis 5', 'Nadpis 6'],
    'Normální',
    (v) => applyStyle(editor, v),
    130,
  );
  styleSel.dataset.role = 'style';
  const styleGroup = el('div', 'ribbon-group');
  styleGroup.append(styleSel, groupLabel('Styly'));
  pane.appendChild(styleGroup);

  // Skupina: Úpravy
  pane.appendChild(group('Úpravy', [
    iconBtn('find', 'Najít', onFindReplace),
    iconBtn('replace', 'Nahradit', onFindReplace),
  ]));
}

// ── Karta Rozložení ──────────────────────────────────────────
function buildLayoutPane(pane: HTMLElement) {
  // Okraje
  const marginSel = dropdown(
    ['Normální', 'Úzké', 'Střední', 'Široké'],
    'Normální',
    (v) => {
      const key = { 'Normální': 'normal', 'Úzké': 'narrow', 'Střední': 'moderate', 'Široké': 'wide' }[v] || 'normal';
      store.patchDoc({ pageSetup: { ...store.get().doc.pageSetup, margins: { ...MARGIN_PRESETS[key] } }, dirty: true });
    },
    130,
  );
  pane.appendChild(labeledGroup('Okraje', 'margins', marginSel));

  // Orientace
  const orientSel = dropdown(
    ['Na výšku', 'Na šířku'],
    'Na výšku',
    (v) => patchPage({ orientation: v === 'Na šířku' ? 'landscape' : 'portrait' }),
    120,
  );
  pane.appendChild(labeledGroup('Orientace', 'orientation', orientSel));

  // Velikost
  const sizeSel = dropdown(
    ['A4', 'Letter', 'Legal'],
    'A4',
    (v) => patchPage({ size: v as PageSetup['size'] }),
    100,
  );
  pane.appendChild(labeledGroup('Velikost', 'pageSize', sizeSel));
}

function patchPage(patch: Partial<PageSetup>) {
  store.patchDoc({ pageSetup: { ...store.get().doc.pageSetup, ...patch }, dirty: true });
}

// ── Karta Vložení ────────────────────────────────────────────
function buildInsertPane(pane: HTMLElement, editor: Editor, onComment: OnComment, onToggleComments: OnToggleComments) {
  // Skupina: Tabulka — tlačítko s výběrem rozměru přes mřížku
  const tableGroup = el('div', 'ribbon-group');
  const tableRow = el('div', 'ribbon-row');
  tableRow.appendChild(tableSizePicker(editor));
  tableGroup.append(tableRow, groupLabel('Tabulka'));
  pane.appendChild(tableGroup);

  // Skupina: Řádky a sloupce
  pane.appendChild(group('Řádky a sloupce', [
    iconBtn('rowAbove', 'Vložit řádek nad', () => editor.chain().focus().addRowBefore().run()),
    iconBtn('rowBelow', 'Vložit řádek pod', () => editor.chain().focus().addRowAfter().run()),
    iconBtn('deleteRow', 'Smazat řádek', () => editor.chain().focus().deleteRow().run()),
    iconBtn('colLeft', 'Vložit sloupec vlevo', () => editor.chain().focus().addColumnBefore().run()),
    iconBtn('colRight', 'Vložit sloupec vpravo', () => editor.chain().focus().addColumnAfter().run()),
    iconBtn('deleteCol', 'Smazat sloupec', () => editor.chain().focus().deleteColumn().run()),
  ]));

  // Skupina: Buňky
  pane.appendChild(group('Buňky', [
    iconBtn('mergeCells', 'Sloučit buňky', () => editor.chain().focus().mergeCells().run()),
    iconBtn('splitCell', 'Rozdělit buňku', () => editor.chain().focus().splitCell().run()),
    iconBtn('deleteTable', 'Smazat tabulku', () => editor.chain().focus().deleteTable().run()),
  ]));

  // Skupina: Ilustrace — vložení obrázku z disku
  pane.appendChild(group('Ilustrace', [
    iconBtn('image', 'Obrázek z tohoto zařízení', () => insertImageFromDisk(editor)),
  ]));

  // Skupina: Stránky
  pane.appendChild(group('Stránky', [
    iconBtn('pageBreak', 'Konec stránky', () => editor.chain().focus().setPageBreak().run()),
  ]));

  // Skupina: Symboly — vložení speciálních Unicode znaků
  const symGroup = el('div', 'ribbon-group');
  const symRow = el('div', 'ribbon-row');
  symRow.appendChild(symbolPicker(editor));
  symGroup.append(symRow, groupLabel('Symboly'));
  pane.appendChild(symGroup);

  // Skupina: Záhlaví a zápatí — číslo stránky s volbou pozice
  const hfGroup = el('div', 'ribbon-group');
  const hfRow = el('div', 'ribbon-row');
  hfRow.appendChild(pageNumberPicker());
  hfGroup.append(hfRow, groupLabel('Záhlaví a zápatí'));
  pane.appendChild(hfGroup);

  // Skupina: Komentáře (Fáze 3)
  const panelBtn = labeledBtn('comment', 'Panel', 'Zobrazit / skrýt panel komentářů', onToggleComments);
  panelBtn.dataset.role = 'comments-toggle'; // main.ts podle toho zvýrazňuje stav
  pane.appendChild(group('Komentáře', [
    labeledBtn('commentAdd', 'Nový', 'Nový komentář k vybranému textu', onComment),
    panelBtn,
  ]));
}

/** Karta Revize (Fáze 3) — sledování změn, panel revizí, komentáře. */
function buildReviewPane(
  pane: HTMLElement,
  editor: Editor,
  onComment: OnComment,
  onToggleTracking: OnToggleTracking,
  onToggleReview: OnToggleReview,
) {
  // ── Skupina: Sledování — přepínač + režim zobrazení + panel ──
  const trackBtn = labeledBtn(
    'trackChanges',
    'Sledovat',
    'Zapnout / vypnout sledování změn',
    onToggleTracking,
  );
  trackBtn.dataset.role = 'tracking-toggle'; // main.ts podle toho zvýrazňuje stav

  const reviewBtn = labeledBtn('review', 'Revize', 'Zobrazit / skrýt panel revizí', onToggleReview);
  reviewBtn.dataset.role = 'review-toggle';

  const trackGroup = el('div', 'ribbon-group');
  const trackRow = el('div', 'ribbon-row');
  trackRow.append(trackBtn, markupViewSelect(), reviewBtn);
  trackGroup.append(trackRow, groupLabel('Sledování'));
  pane.appendChild(trackGroup);

  // ── Skupina: Změny — přijmout/odmítnout + navigace ──
  pane.appendChild(group('Změny', [
    labeledBtn('check', 'Přijmout', 'Přijmout změnu pod kurzorem', () => {
      if (!acceptAtSelection(editor)) alert('Kurzor není na žádné sledované změně.');
    }),
    labeledBtn('close', 'Odmítnout', 'Odmítnout změnu pod kurzorem', () => {
      if (!rejectAtSelection(editor)) alert('Kurzor není na žádné sledované změně.');
    }),
    labeledBtn('chevronUp', 'Předchozí', 'Přejít na předchozí změnu', () => {
      if (!goToPrevChange(editor)) alert('Dokument neobsahuje žádné sledované změny.');
    }),
    labeledBtn('chevronDown', 'Další', 'Přejít na další změnu', () => {
      if (!goToNextChange(editor)) alert('Dokument neobsahuje žádné sledované změny.');
    }),
  ]));

  // ── Skupina: Komentáře ──
  pane.appendChild(group('Komentáře', [
    labeledBtn('commentAdd', 'Nový', 'Nový komentář k vybranému textu', onComment),
  ]));
}

/** Dropdown režimu zobrazení revizí (jako „All Markup" ve Wordu). */
function markupViewSelect(): HTMLElement {
  const sel = el('select', 'markup-select') as HTMLSelectElement;
  sel.title = 'Jak zobrazovat sledované změny';
  (Object.keys(MARKUP_VIEW_LABELS) as MarkupView[]).forEach((v) => {
    const o = document.createElement('option');
    o.value = v;
    o.textContent = MARKUP_VIEW_LABELS[v];
    sel.appendChild(o);
  });
  sel.value = getMarkupView();
  sel.addEventListener('change', () => setMarkupView(sel.value as MarkupView));
  return sel;
}

/** Rozbalovací volba automatického čísla stránky (umístění × zarovnání). */
function pageNumberPicker(): HTMLElement {
  const wrap = el('div', 'symbol-picker-wrap');
  const btn = el('button', 'ribbon-btn') as HTMLButtonElement;
  btn.title = 'Číslo stránky';
  btn.innerHTML = icon('pageNumber');

  const pop = el('div', 'pagenum-pop');
  pop.style.display = 'none';

  const cur = () => store.get().doc.pageNumber || DEFAULT_PAGE_NUMBER;

  function apply(patch: Partial<PageNumberSetup>) {
    const next = { ...cur(), ...patch, enabled: true };
    store.patchDoc({ pageNumber: next, dirty: true });
    render();
  }
  function disable() {
    store.patchDoc({ pageNumber: { ...cur(), enabled: false }, dirty: true });
    render();
  }

  const body = el('div', 'pagenum-body');

  function render() {
    const c = cur();
    body.innerHTML = '';

    const placeRow = el('div', 'pagenum-row');
    placeRow.append(rowLabel('Umístění'));
    (['header', 'footer'] as const).forEach((pl) => {
      const b = el('button', 'pagenum-opt') as HTMLButtonElement;
      b.textContent = pl === 'header' ? 'Záhlaví' : 'Zápatí';
      b.classList.toggle('on', c.enabled && c.place === pl);
      b.onclick = () => apply({ place: pl });
      placeRow.appendChild(b);
    });

    const alignRow = el('div', 'pagenum-row');
    alignRow.append(rowLabel('Zarovnání'));
    (['left', 'center', 'right'] as const).forEach((al) => {
      const b = el('button', 'pagenum-opt') as HTMLButtonElement;
      b.textContent = al === 'left' ? 'Vlevo' : al === 'center' ? 'Střed' : 'Vpravo';
      b.classList.toggle('on', c.enabled && c.align === al);
      b.onclick = () => apply({ align: al });
      alignRow.appendChild(b);
    });

    const offRow = el('div', 'pagenum-row');
    const off = el('button', 'pagenum-off') as HTMLButtonElement;
    off.textContent = c.enabled ? 'Odebrat číslo stránky' : 'Číslo stránky vypnuto';
    off.disabled = !c.enabled;
    off.onclick = () => disable();
    offRow.appendChild(off);

    body.append(placeRow, alignRow, offRow);
  }
  render();

  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    pop.style.display = pop.style.display === 'none' ? 'block' : 'none';
  });
  document.addEventListener('click', (e) => {
    if (!wrap.contains(e.target as Node)) pop.style.display = 'none';
  });

  pop.appendChild(body);
  wrap.append(btn, pop);
  return wrap;
}

function rowLabel(text: string): HTMLElement {
  const l = el('span', 'pagenum-rowlabel');
  l.textContent = text;
  return l;
}

/** Tlačítko Symbol s rozbalovacím panelem (taby kategorií + mřížka znaků). */
function symbolPicker(editor: Editor): HTMLElement {
  const wrap = el('div', 'symbol-picker-wrap');

  const btn = el('button', 'ribbon-btn') as HTMLButtonElement;
  btn.title = 'Vložit symbol';
  btn.innerHTML = icon('symbol');

  const pop = el('div', 'symbol-picker-pop');
  pop.style.display = 'none';

  const tabs = el('div', 'symbol-picker-tabs');
  const grid = el('div', 'symbol-picker-grid');

  let activeCat = SYMBOL_CATEGORIES[0].id;

  function renderGrid() {
    grid.innerHTML = '';
    const cat = SYMBOL_CATEGORIES.find((c) => c.id === activeCat) || SYMBOL_CATEGORIES[0];
    cat.chars.forEach((ch) => {
      const cell = el('button', 'symbol-picker-cell') as HTMLButtonElement;
      cell.type = 'button';
      cell.textContent = ch;
      cell.title = ch;
      cell.addEventListener('mousedown', (e) => e.preventDefault());
      cell.addEventListener('click', () => {
        editor.chain().focus().insertContent(ch).run();
        // Panel necháme otevřený — uživatel často vkládá víc znaků za sebou.
      });
      grid.appendChild(cell);
    });
  }

  SYMBOL_CATEGORIES.forEach((cat) => {
    const tab = el('button', 'symbol-picker-tab') as HTMLButtonElement;
    tab.type = 'button';
    tab.textContent = cat.label;
    tab.classList.toggle('on', cat.id === activeCat);
    tab.addEventListener('mousedown', (e) => e.preventDefault());
    tab.addEventListener('click', () => {
      activeCat = cat.id;
      tabs.querySelectorAll('.symbol-picker-tab').forEach((t) => t.classList.remove('on'));
      tab.classList.add('on');
      renderGrid();
    });
    tabs.appendChild(tab);
  });
  renderGrid();

  function open() { pop.style.display = 'block'; }
  function close() { pop.style.display = 'none'; }

  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    pop.style.display === 'none' ? open() : close();
  });
  document.addEventListener('click', (e) => {
    if (!wrap.contains(e.target as Node)) close();
  });

  pop.append(tabs, grid);
  wrap.append(btn, pop);
  return wrap;
}

/** Otevře výběr obrázku a vloží ho do dokumentu s rozumnou výchozí velikostí. */
async function insertImageFromDisk(editor: Editor) {
  try {
    const picked = await pickImage();
    if (!picked) return;
    const size = initialSize(picked.width, picked.height);
    editor
      .chain()
      .focus()
      .setImage({
        src: picked.dataUrl,
        alt: picked.name,
        width: size?.width,
        height: size?.height,
        wrap: 'inline',
        align: 'left',
      })
      .run();
  } catch (err) {
    console.error('Vložení obrázku selhalo:', err);
  }
}

/** Tlačítko Tabulka s rozbalovací mřížkou pro výběr rozměru (jako Word). */
function tableSizePicker(editor: Editor): HTMLElement {
  const wrap = el('div', 'table-picker-wrap');

  const btn = el('button', 'ribbon-btn') as HTMLButtonElement;
  btn.title = 'Vložit tabulku';
  btn.innerHTML = icon('table');

  const pop = el('div', 'table-picker-pop');
  pop.style.display = 'none';

  const MAXR = 8, MAXC = 10;
  const label = el('div', 'table-picker-label');
  label.textContent = 'Vložit tabulku';
  const grid = el('div', 'table-picker-grid');
  const cells: HTMLElement[] = [];

  for (let r = 0; r < MAXR; r++) {
    for (let c = 0; c < MAXC; c++) {
      const cell = el('div', 'table-picker-cell');
      cell.dataset.r = String(r + 1);
      cell.dataset.c = String(c + 1);
      cell.addEventListener('mouseenter', () => highlight(r + 1, c + 1));
      cell.addEventListener('click', () => {
        editor.chain().focus().insertTable({ rows: r + 1, cols: c + 1, withHeaderRow: true }).run();
        close();
      });
      grid.appendChild(cell);
      cells.push(cell);
    }
  }

  function highlight(rows: number, cols: number) {
    cells.forEach((cell) => {
      const cr = +cell.dataset.r!;
      const cc = +cell.dataset.c!;
      cell.classList.toggle('on', cr <= rows && cc <= cols);
    });
    label.textContent = `Tabulka ${cols} × ${rows}`;
  }

  function open() { pop.style.display = 'block'; }
  function close() { pop.style.display = 'none'; }

  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    pop.style.display === 'none' ? open() : close();
  });
  // Zavři při kliknutí jinam.
  document.addEventListener('click', (e) => {
    if (!wrap.contains(e.target as Node)) close();
  });

  pop.append(label, grid);
  wrap.append(btn, pop);
  return wrap;
}

// ═══════════════════════════════════════════════════════════════
// Helpery pro tvorbu prvků ribbonu
// ═══════════════════════════════════════════════════════════════
function el(tag: string, cls?: string): HTMLElement {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  return e;
}

function group(label: string, children: HTMLElement[]): HTMLElement {
  const g = el('div', 'ribbon-group');
  const row = el('div', 'ribbon-row');
  children.forEach((c) => row.appendChild(c));
  g.append(row, groupLabel(label));
  return g;
}

function labeledGroup(label: string, ic: IconName, control: HTMLElement): HTMLElement {
  const g = el('div', 'ribbon-group');
  const row = el('div', 'ribbon-row');
  const ib = el('span', 'ribbon-inline-icon');
  ib.innerHTML = icon(ic, 20);
  row.append(ib, control);
  g.append(row, groupLabel(label));
  return g;
}

function groupLabel(text: string): HTMLElement {
  const l = el('div', 'ribbon-group-label');
  l.textContent = text;
  return l;
}

function iconBtn(ic: IconName, title: string, onClick: () => void): HTMLElement {
  const b = el('button', 'ribbon-btn');
  b.title = title;
  b.innerHTML = icon(ic);
  b.onclick = onClick;
  return b;
}

/**
 * Tlačítko s ikonou A viditelným popiskem pod ní. Používáme tam, kde by
 * samotná ikona nebyla srozumitelná (např. přepínač panelu komentářů).
 */
function labeledBtn(ic: IconName, label: string, title: string, onClick: () => void): HTMLElement {
  const b = el('button', 'ribbon-btn ribbon-btn-labeled');
  b.title = title;
  b.innerHTML = `${icon(ic)}<span class="ribbon-btn-label">${label}</span>`;
  b.onclick = onClick;
  return b;
}

function cmdBtn(ic: IconName, title: string, onClick: () => void): HTMLElement {
  return iconBtn(ic, title, onClick);
}

function markBtn(ic: IconName, title: string, mark: string, editor: Editor): HTMLElement {
  const b = el('button', 'ribbon-btn') as HTMLButtonElement;
  b.title = title;
  b.innerHTML = icon(ic);
  b.dataset.mark = mark;
  b.onclick = () => {
    const map: Record<string, () => void> = {
      bold: () => editor.chain().focus().toggleBold().run(),
      italic: () => editor.chain().focus().toggleItalic().run(),
      underline: () => editor.chain().focus().toggleUnderline().run(),
      strike: () => editor.chain().focus().toggleStrike().run(),
      subscript: () => editor.chain().focus().toggleSubscript().run(),
      superscript: () => editor.chain().focus().toggleSuperscript().run(),
    };
    map[mark]?.();
  };
  return b;
}

function alignBtn(ic: IconName, title: string, align: string, editor: Editor): HTMLElement {
  const b = el('button', 'ribbon-btn') as HTMLButtonElement;
  b.title = title;
  b.innerHTML = icon(ic);
  b.dataset.align = align;
  b.onclick = () => editor.chain().focus().setTextAlign(align).run();
  return b;
}

function toggleBtn(
  ic: IconName, title: string, key: string,
  handlers: { onClick: () => void; onDblClick: () => void },
): HTMLElement {
  const b = el('button', 'ribbon-btn') as HTMLButtonElement;
  b.title = title;
  b.innerHTML = icon(ic);
  b.dataset.toggle = key;
  b.onclick = handlers.onClick;
  b.ondblclick = handlers.onDblClick;
  return b;
}

function colorBtn(ic: IconName, title: string, onPick: (c: string) => void): HTMLElement {
  const wrap = el('span', 'ribbon-color') as HTMLElement;
  const b = el('button', 'ribbon-btn') as HTMLButtonElement;
  b.title = title;
  b.innerHTML = icon(ic);
  const input = document.createElement('input');
  input.type = 'color';
  input.className = 'ribbon-color-input';
  input.oninput = () => onPick(input.value);
  b.onclick = () => input.click();
  wrap.append(b, input);
  return wrap;
}

function dropdown(options: string[], initial: string, onChange: (v: string) => void, width = 120): HTMLElement {
  const sel = document.createElement('select');
  sel.className = 'ribbon-select';
  sel.style.width = width + 'px';
  options.forEach((o) => {
    const opt = document.createElement('option');
    opt.value = o;
    opt.textContent = o;
    if (o === initial) opt.selected = true;
    sel.appendChild(opt);
  });
  sel.onchange = () => onChange(sel.value);
  return sel;
}

// ── Font size přes vlastní FontSize extension ──
function setFontSize(editor: Editor, size: string) {
  editor.chain().focus().setFontSize(size + 'pt').run();
}

function applyStyle(editor: Editor, style: string) {
  const c = editor.chain().focus();
  if (style === 'Normální') { c.setParagraph().run(); return; }
  const level = parseInt(style.replace(/\D/g, ''), 10) as 1 | 2 | 3 | 4 | 5 | 6;
  c.toggleHeading({ level }).run();
}

// ── Reaktivní zvýraznění aktivních tlačítek ──────────────────
function refreshActiveStates(root: HTMLElement, editor: Editor) {
  root.querySelectorAll('[data-mark]').forEach((b) => {
    const mark = (b as HTMLElement).dataset.mark!;
    b.classList.toggle('active', editor.isActive(mark));
  });
  root.querySelectorAll('[data-align]').forEach((b) => {
    const align = (b as HTMLElement).dataset.align!;
    b.classList.toggle('active', editor.isActive({ textAlign: align }));
  });

  // Synchronizace dropdownů se stavem kurzoru (jako ve Wordu).
  const fontSel = root.querySelector('[data-role="font"]') as HTMLSelectElement | null;
  if (fontSel) {
    const raw = editor.getAttributes('textStyle').fontFamily as string | undefined;
    // TipTap ukládá font s uvozovkami ('"Times New Roman"') a může jich být
    // víc (fallback stack) — vezmi první a zbav uvozovek.
    const f = raw ? raw.split(',')[0].replace(/['"]/g, '').trim() : '';
    fontSel.value = f && [...fontSel.options].some((o) => o.value === f) ? f : 'Times New Roman';
  }
  const sizeSel = root.querySelector('[data-role="size"]') as HTMLSelectElement | null;
  if (sizeSel) {
    const sz = editor.getAttributes('textStyle').fontSize as string | undefined; // '10pt'
    const num = sz ? String(parseInt(sz, 10)) : '11';
    sizeSel.value = [...sizeSel.options].some((o) => o.value === num) ? num : '11';
  }
  const styleSel = root.querySelector('[data-role="style"]') as HTMLSelectElement | null;
  if (styleSel) {
    let val = 'Normální';
    for (let lvl = 1; lvl <= 6; lvl++) {
      if (editor.isActive('heading', { level: lvl })) { val = 'Nadpis ' + lvl; break; }
    }
    styleSel.value = val;
  }
}
